import { DestroyRef, ElementRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Fixture, FixtureType } from '@cds/betting-offer';
import { FixtureRequest, FixtureState, OfferMapping, SortByCriteria } from '@cds/query-objects';
import { DetailedFixtureFactory } from '@frontend/sports/betting-offer/feature/fixture-factories';
import { EnhancedFixtureViewGroupingConfiguration } from '@frontend/sports/betting-offer/feature/model';
import { OfferGroupingService } from '@frontend/sports/betting-offer/feature/offer-grouping';
import { BettingOfferApi } from '@frontend/sports/betting-offer/feature/offer-service';
import { IntersectionObserverService } from '@frontend/sports/common/core/utils/dom';
import { trackingConstants } from '@frontend/sports/tracking/feature';
import { TimerService } from '@frontend/vanilla/core';
import { Store } from '@ngrx/store';
import { Observable, debounceTime, distinctUntilChanged, filter, map, switchMap } from 'rxjs';

import { EventDetailsService } from '../event-details-common/event-details.service';
import { QuickLinkItem } from '../quick-links/quick-links.models';
import { OptionSheetViewActions } from '../sheet-view-core/sheet-view.actions';

@Injectable({ providedIn: 'root' })
export class PromoListItemSheetViewService {
    private intersectionObserverService = inject(IntersectionObserverService);
    private bettingOfferApi = inject(BettingOfferApi);
    private offerGroupingService = inject(OfferGroupingService);
    private detailedFixtureFactory = inject(DetailedFixtureFactory);
    private eventDetailsService = inject(EventDetailsService);
    private store = inject(Store);
    private timerService = inject(TimerService);

    registerQuickLinkPrefetch(element: ElementRef, item: QuickLinkItem, destroyRef: DestroyRef): void {
        this.intersectionObserverService
            .observe(element)
            .pipe(
                debounceTime(100),
                map((entry) => entry.isIntersecting),
                distinctUntilChanged(),
                filter((inView) => inView),
                takeUntilDestroyed(destroyRef),
            )
            .subscribe(() => {
                this.timerService.scheduleIdleCallback(() => {
                    this.fetchFixtureDataAndGrouping(item).subscribe();
                });
            });
    }

    fetchFixtureDataAndGrouping(
        item: QuickLinkItem,
    ): Observable<{ fixture: Fixture; grouping: EnhancedFixtureViewGroupingConfiguration | undefined }> {
        const fixtureReq: FixtureRequest = {
            fixtureTypes: FixtureType.Standard,
            state: FixtureState.Latest,
            offerMapping: OfferMapping.Filtered,
            dynamicOfferCategories: item.dynamicOfferCategory,
            sportIds: item.sportId?.toString(),
            competitionIds: item.leagueId?.toString(),
            sortBy: SortByCriteria.FixtureStage,
            take: 1,
        };

        return this.bettingOfferApi.getFixtureList(fixtureReq).pipe(
            map((res) => res?.fixtures?.[0]),
            filter((fixture): fixture is Fixture => !!fixture),
            switchMap((fixture) =>
                this.offerGroupingService.getFixtureGrouping(fixture.sport.id, 'any').pipe(map((grouping) => ({ fixture, grouping }))),
            ),
        );
    }

    openSheetView(fixture: Fixture, grouping: EnhancedFixtureViewGroupingConfiguration, dynamicOfferCategory?: string, pageName?: string) {
        const event = this.detailedFixtureFactory.create({ fixture, splitFixtures: [], grouping });
        const optionGroups = this.eventDetailsService.prepareEventOptionGroups({ event });

        this.store.dispatch(
            OptionSheetViewActions.openOptionSheetView({
                payload: {
                    sourceEvent: event,
                    sourceOptionGroup: optionGroups[0],
                    dynamicOfferCategory,
                    subscribeSourceEvent: true,
                    ...(pageName && {
                        tracking: `${trackingConstants.QUICKLINKS}/${pageName}/${trackingConstants.SPORT}/${event.sport.id}`,
                    }),
                },
            }),
        );
    }
}
